import React, { useRef, useState, useEffect, useCallback } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import {
  Section,
  SectionHeading,
  CarouselWrapper,
  Track,
  QuoteCard,
  QuoteMark,
  QuoteText,
  Attribution,
  AttrName,
  AttrTitle,
  AttrOrg,
  NavRow,
  NavButton,
  Dots,
  Dot
} from "./styled.components";

const stories = [
  {
    quote:
      "Because our district stocked emergency medication, I was able to administer epinephrine while the front office called 911. He was treated at the hospital and went home the same day.",
    name: "Tracey M.",
    title: "Elementary School Nurse",
    org: "Deer Valley Unified School District, AZ"
  },
  {
    quote:
      "A staff member was exposed to peanuts and their epi-pen was inaccessible. I remained calm, utilized our stock epinephrine, and successfully administered it, despite never having used an epi-pen before.",
    name: "Anonymous",
    title: "Health Office Staff",
    org: "Arizona School District"
  },
  {
    quote:
      "If a student has a reaction, I feel prepared. Parents are happy we have stock epi-pens on school campus. Kyah's EPICourse gave me and our entire team the confidence to respond effectively.",
    name: "Kim R.",
    title: "School Nurse",
    org: "Deer Valley Unified School District, AZ"
  },
  {
    quote:
      "A student had a peanut allergy and needed an EpiPen. Because of Kyah's EPICourse, I was prepared. My school had the medication stocked and I had the training to act immediately.",
    name: "Anonymous",
    title: "Health Aide",
    org: "Arizona School District"
  },
  {
    quote:
      "After the course, I trained our staff on how to respond to emergency situations. New parents with incoming 9th graders now ask specifically about allergies and what we have in place. They feel comfort knowing we are prepared.",
    name: "Katelyn P.",
    title: "Health Staff",
    org: "Agua Fria Union High School District, AZ"
  },
  {
    quote:
      "As someone with food allergies, my internship with KRF has been incredibly rewarding. I contributed to expanding food allergy awareness, strengthening emergency preparedness, and improving access to epinephrine. This experience deepened my commitment to advancing community wellness and health equity.",
    name: "Michelle L., MPH",
    title: "Development Team Intern",
    org: "Graduate, University of Arizona Mel & Enid Zuckerman College of Public Health"
  },
  {
    quote:
      "After first interning with KRF as an undergraduate, I chose to return while pursuing my MPH due to the meaningful experience I had. My decision to return reflects the impact of this organization not only on the communities it serves, but also on the development of future public health professionals.",
    name: "Taylor H.",
    title: "MPH Candidate, Applied Epidemiology Intern",
    org: "University of Arizona Mel & Enid Zuckerman College of Public Health"
  },
  {
    quote:
      "Reviewing testimonials from school personnel was particularly impactful. Many shared how the training increased both their knowledge and their ability to act in emergencies. As a public health professional and father of three school-aged children, I value this work deeply.",
    name: "Anthony M.",
    title: "MPH Candidate, Policy Team Lead Intern",
    org: "University of Arizona Mel & Enid Zuckerman College of Public Health"
  }
];

const AUTO_ADVANCE_MS = 6000;
const MD_BREAKPOINT = 768;

const RealStories: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(3);
  const totalPages = Math.ceil(stories.length / cardsPerPage);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const update = () => {
      setCardsPerPage(window.innerWidth < MD_BREAKPOINT ? 1 : 3);
      setPage(0);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const scrollToPage = useCallback((p: number) => {
    if (!trackRef.current) return;
    trackRef.current.scrollTo({
      left: p * trackRef.current.offsetWidth,
      behavior: "smooth"
    });
    setPage(p);
  }, []);

  const goNext = useCallback(() => {
    scrollToPage((page + 1) % totalPages);
  }, [page, totalPages, scrollToPage]);

  const goPrev = useCallback(() => {
    scrollToPage((page - 1 + totalPages) % totalPages);
  }, [page, totalPages, scrollToPage]);

  useEffect(() => {
    timerRef.current = setInterval(goNext, AUTO_ADVANCE_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [goNext]);

  const handleManualNav = (fn: () => void) => {
    if (timerRef.current) clearInterval(timerRef.current);
    fn();
    timerRef.current = setInterval(goNext, AUTO_ADVANCE_MS);
  };

  return (
    <Section>
      <SectionHeading>
        Real Stories. Real Preparedness. Real Impact.
      </SectionHeading>
      <CarouselWrapper>
        <Track ref={trackRef}>
          {stories.map((s, i) => (
            <QuoteCard key={i}>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>{s.quote}</QuoteText>
              <Attribution>
                <AttrName>{s.name}</AttrName>
                <AttrTitle>{s.title}</AttrTitle>
                <AttrOrg>{s.org}</AttrOrg>
              </Attribution>
            </QuoteCard>
          ))}
        </Track>
        <NavRow>
          <NavButton
            onClick={() => handleManualNav(goPrev)}
            aria-label="Previous"
          >
            <FaArrowLeft />
          </NavButton>
          <Dots>
            {Array.from({ length: totalPages }).map((_, i) => (
              <Dot
                key={i}
                active={i === page}
                onClick={() => handleManualNav(() => scrollToPage(i))}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </Dots>
          <NavButton onClick={() => handleManualNav(goNext)} aria-label="Next">
            <FaArrowRight />
          </NavButton>
        </NavRow>
      </CarouselWrapper>
    </Section>
  );
};

export default RealStories;
